import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { BottomNav } from "@/components/BottomNav";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useStandings, type StandingRow } from "@/hooks/useStandings";
import { useT } from "@/i18n";
import { shortCodeFor } from "@/lib/flags";
import { Trophy } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BracketView } from "@/components/BracketView";
import { useDragScroll } from "@/hooks/useDragScroll";
import { useApp, getEffectiveMatches } from "@/store/app";


export const Route = createFileRoute("/standings")({
  component: StandingsPage,
});

function StandingsPage() {
  const t = useT();
  const overrides = useApp((s) => s.matchOverrides);
  const [tab, setTab] = useState<"groups" | "bracket">(() => {
    const hasActiveGroupGames = getEffectiveMatches(overrides).some(
      (m) => m.stage === "group" && (m.status === "upcoming" || m.status === "live")
    );
    return hasActiveGroupGames ? "groups" : "bracket";
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-6xl px-4 pt-6 pb-24">

        <div className="mb-5 flex items-center gap-2">
          <Trophy className="h-6 w-6 text-premium" />
          <h1 className="text-2xl font-bold tracking-tight">
            {t("Standings", "Clasament")}
          </h1>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "groups" | "bracket")}>
          <TabsList className="mb-5 grid w-full grid-cols-2">
            <TabsTrigger value="groups">{t("Groups", undefined, "Grupe")}</TabsTrigger>
            <TabsTrigger value="bracket">
              {t("Bracket", undefined, "Tablou eliminatoriu")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="groups">
            <GroupsTab />
          </TabsContent>
          <TabsContent value="bracket">
            <BracketView />
          </TabsContent>
        </Tabs>
      </main>
      <BottomNav />
      <ScrollToTop />
    </div>
  );
}

const isSlotPlaceholder = (name: string) =>
  /^[0-9][A-Z]$/.test(name) || /^3rd-/.test(name) || /^W\d+$/.test(name);

function GroupsTab() {
  const t = useT();
  const { data, isLoading, error } = useStandings();
  const overrides = useApp((s) => s.matchOverrides);

  const teamsInBracket = useMemo(() => {
    const set = new Set<string>();
    for (const m of getEffectiveMatches(overrides)) {
      if (m.stage === "group") continue;
      if (!isSlotPlaceholder(m.homeTeam)) set.add(m.homeTeam);
      if (!isSlotPlaceholder(m.awayTeam)) set.add(m.awayTeam);
    }
    return set;
  }, [overrides]);

  const groups: Record<string, StandingRow[]> = {};
  for (const row of data ?? []) {
    (groups[row.group_letter] ??= []).push(row);
  }
  const letters = Object.keys(groups).sort();

  return (
    <>
      {isLoading && (
        <div className="rounded-2xl border bg-card p-6 text-center text-sm text-muted-foreground">
          {t("Loading…", "Se încarcă…")}
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

      <div className="space-y-6">
        {letters.map((letter) => (
          <GroupTable key={letter} letter={letter} rows={groups[letter]} teamsInBracket={teamsInBracket} />
        ))}
      </div>
    </>
  );
}

function GroupTable({ letter, rows, teamsInBracket }: { letter: string; rows: StandingRow[]; teamsInBracket: Set<string> }) {
  const t = useT();
  const dragRef = useDragScroll<HTMLDivElement>();
  return (
    <section className="overflow-hidden rounded-2xl border bg-card shadow-sm">
      <div className="border-b bg-muted/40 px-4 py-3">
        <h2 className="text-base font-bold tracking-tight text-premium">
          {t("Group", "Grupa")} {letter}
        </h2>
      </div>

      <div className="flex">
        {/* Frozen columns: # + Team */}
        <div className="shrink-0 border-r bg-card">
          <table className="text-sm">
            <thead className="bg-muted/20 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="h-9 w-8 px-2 text-left">#</th>
                <th className="h-9 px-2 text-left">{t("Team", "Echipă")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const qualifies = idx < 2 || (idx === 2 && teamsInBracket.has(r.team_name));
                return (
                  <tr
                    key={r.team_code}
                    className={`border-t ${qualifies ? "bg-premium/[0.06]" : ""}`}
                  >
                    <td
                      className={`h-11 w-8 px-2 text-xs font-semibold text-muted-foreground ${
                        qualifies ? "border-l-2 border-premium" : "border-l-2 border-transparent"
                      }`}
                    >
                      {idx + 1}
                    </td>
                    <td className="h-11 px-2">
                      <div className="flex items-center gap-2">
                        {r.flag_url && (
                          <img
                            src={r.flag_url}
                            alt=""
                            className="h-4 w-6 rounded-sm object-cover shadow-sm ring-1 ring-black/10"
                            loading="lazy"
                          />
                        )}
                        <span className="font-semibold tabular-nums">
                          {shortCodeFor(r.team_name)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Scrollable stats */}
        <div ref={dragRef} className="min-w-0 flex-1 cursor-grab overflow-x-auto">
          <table className="w-full min-w-[300px] text-sm">
            <thead className="bg-muted/20 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="h-9 px-2 text-center">{t("P", "E")}</th>
                <th className="h-9 px-2 text-center">{t("W", "V")}</th>
                <th className="h-9 px-2 text-center">{t("D", "Eg")}</th>
                <th className="h-9 px-2 text-center">{t("L", "Î")}</th>
                <th className="h-9 px-2 text-center">{t("GF", "GM")}</th>
                <th className="h-9 px-2 text-center">{t("GA", "GP")}</th>
                <th className="h-9 px-2 text-center">{t("GD", "DG")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const qualifies = idx < 2 || (idx === 2 && teamsInBracket.has(r.team_name));
                return (
                  <tr
                    key={r.team_code}
                    className={`border-t ${qualifies ? "bg-premium/[0.06]" : ""}`}
                  >
                    <td className="h-11 px-2 text-center tabular-nums">{r.played}</td>
                    <td className="h-11 px-2 text-center tabular-nums">{r.won}</td>
                    <td className="h-11 px-2 text-center tabular-nums">{r.drawn}</td>
                    <td className="h-11 px-2 text-center tabular-nums">{r.lost}</td>
                    <td className="h-11 px-2 text-center tabular-nums">{r.gf}</td>
                    <td className="h-11 px-2 text-center tabular-nums">{r.ga}</td>
                    <td className="h-11 px-2 text-center tabular-nums">
                      {r.gd > 0 ? `+${r.gd}` : r.gd}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Frozen right: PTS — always visible */}
        <div className="shrink-0 border-l bg-card">
          <table className="text-sm">
            <thead className="bg-muted/20 text-[10px] font-semibold uppercase tracking-wider">
              <tr>
                <th className="h-9 px-3 text-center font-bold text-premium">
                  {t("Pts", "Pct")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const qualifies = idx < 2 || (idx === 2 && teamsInBracket.has(r.team_name));
                return (
                  <tr
                    key={r.team_code}
                    className={`border-t ${qualifies ? "bg-premium/[0.06]" : ""}`}
                  >
                    <td className="h-11 px-3 text-center font-bold tabular-nums text-premium">
                      {r.pts}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
